import type { ComponentPropsWithoutRef, Ref } from 'react';
import { Accordion as BaseAccordion } from '@base-ui/react/accordion';
import { omit } from 'lodash-es';
import { useMemo } from 'react';

import { selectors } from '../../common/selectors';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { Badge } from '../Badge/Badge';

type BaseAccordionRootProps = ComponentPropsWithoutRef<typeof BaseAccordion.Root>;

type AccordionSingleProps = {
  type?: 'single';
  collapsible?: boolean;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

type AccordionMultipleProps = {
  type: 'multiple';
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
};

export type AccordionProps = (AccordionSingleProps | AccordionMultipleProps) &
  Omit<BaseAccordionRootProps, 'value' | 'defaultValue' | 'onValueChange' | 'multiple'>;

function toBaseAccordionValue(
  value: string | string[] | undefined,
  multiple: boolean,
): string[] | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (multiple) {
    return value as string[];
  }

  const singleValue = value as string;
  return singleValue === '' ? [] : [singleValue];
}

function AccordionRoot({
  type = 'single',
  value,
  defaultValue,
  onValueChange,
  ...rest
}: AccordionProps) {
  const multiple = type === 'multiple';
  const collapsible = multiple ? false : (rest as AccordionSingleProps).collapsible ?? false;
  const props = omit(rest, ['collapsible']);
  const baseValue = useMemo(() => toBaseAccordionValue(value, multiple), [value, multiple]);
  const baseDefaultValue = useMemo(
    () => toBaseAccordionValue(defaultValue, multiple),
    [defaultValue, multiple],
  );

  const handleValueChange: NonNullable<BaseAccordionRootProps['onValueChange']> = (
    nextValue,
    eventDetails,
  ) => {
    const nextStringValues = nextValue as string[];

    if (!multiple && !collapsible && nextStringValues.length === 0) {
      eventDetails.cancel();
      return;
    }

    if (multiple) {
      (onValueChange as AccordionMultipleProps['onValueChange'] | undefined)?.(nextStringValues);
      return;
    }

    (onValueChange as AccordionSingleProps['onValueChange'] | undefined)?.(
      nextStringValues[0] ?? '',
    );
  };

  return (
    <BaseAccordion.Root
      {...props}
      multiple={multiple}
      value={baseValue}
      defaultValue={baseDefaultValue}
      onValueChange={onValueChange ? handleValueChange : undefined}
    />
  );
}

const AccordionTriggerHeader = styled(BaseAccordion.Header, {
  all: 'unset',
  display: 'flex',
  padding: '$2 $0',
  justifyContent: 'space-between',
});
AccordionTriggerHeader.displayName = 'AccordionTriggerHeader';

const AccordionTriggerLabel = styled(Text, {
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },
});
AccordionTriggerLabel.displayName = 'AccordionTriggerLabel';

const AccordionTriggerButton = styled(BaseAccordion.Trigger, {
  [selectors.focus]: {
    outline: 'none',
  },
});
AccordionTriggerButton.displayName = 'AccordionTriggerButton';

const AccordionTriggerChevron = styled(UtilityControl, {
  '[data-panel-open] &': { transform: 'rotate(90deg)' },
});
AccordionTriggerChevron.displayName = 'AccordionTriggerChevron';

type AccordionTriggerProps = ComponentPropsWithoutRef<typeof BaseAccordion.Trigger> & {
  count?: number;
  ref?: Ref<HTMLButtonElement | null>;
};

export function AccordionTrigger({
  children,
  count,
  ref: forwardedRef,
  ...remaining
}: AccordionTriggerProps) {
  return (
    <AccordionTriggerHeader>
      <AccordionTriggerButton
        tabIndex={-1}
        {...remaining}
        ref={forwardedRef}
        render={(props, state) => (
          <button {...props} type="button">
            <AlignStack
              direction="row"
              align="center"
              gap={6}
              preset="body"
              width="fit-content"
              start={
                <AccordionTriggerChevron
                  aria-hidden
                  icon="chevron-right"
                  label={state.open ? 'Collapse' : 'Expand'}
                  variant="neutral"
                />
              }
              end={
                count ? (
                  <Badge ends="card" size="small" variant="neutral">
                    {count}
                  </Badge>
                ) : undefined
              }
            >
              <AccordionTriggerLabel weight="bold">{children}</AccordionTriggerLabel>
            </AlignStack>
          </button>
        )}
      />
    </AccordionTriggerHeader>
  );
}

export const AccordionContent = styled(BaseAccordion.Panel, {
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },
});
AccordionContent.displayName = 'AccordionContent';

export const AccordionItem = styled(BaseAccordion.Item, {
  display: 'flex',
  flexDirection: 'column',
  padding: '$8 $16',
  strokeBottom: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    strokeBottom: colors.strokeNeutralDark,
  },

  '&[data-open]': { gap: '$4' },
});
AccordionItem.displayName = 'AccordionItem';

export const Accordion = styled(AccordionRoot, {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '$8',
  strokeAll: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    strokeAll: colors.strokeNeutralDark,
  },

  [`& ${AccordionItem}:last-child`]: {
    strokeBottom: colors.transparent,
  },
});
Accordion.displayName = 'Accordion';
